import { PublicKey } from "@solana/web3.js";
import { WellScene, levelToFraction, prefersReducedMotion } from "../well-scene.js";
import {
  connection,
  fetchTokenState,
  fetchJsonMeta,
  wellPda,
  parseWell,
  wsAccountSubscribeSupported,
  parseEvents,
  chainNow,
  syncClock,
  currentFeeBps,
  quoteBuy,
  quoteSell,
  priceSol,
  marketCapSol,
  curveProgress,
  pendingWater,
  previewDrip,
  MIN_STAKE,
} from "../chain.js";
import { swap, stake, unstake, claim } from "../actions.js";
import { walletKey, onWalletChange, connectWallet } from "../wallet.js";
import { explorerTx, explorerAddr } from "../config.js";
import {
  $,
  esc,
  short,
  fmtSol,
  fmtTokens,
  fmtNum,
  fmtPrice,
  fmtBps,
  fmtAge,
  fmtPct,
  parseUnits,
  formatUnits,
  runTx,
  toast,
  safeUrl,
  placeholderArt,
} from "../ui.js";
import { describeError } from "../errors.js";

const ATA_RENT = 2_039_280n;
const TX_FEE_BUFFER = 1_000_000n; // network + priority fee headroom
const SLIP_KEY = "well.slippageBps";

function loadSlippage() {
  try {
    const v = Number(localStorage.getItem(SLIP_KEY));
    return v > 0 && v <= 5000 ? v : 300;
  } catch {
    return 300;
  }
}
function saveSlippage(v) {
  try {
    localStorage.setItem(SLIP_KEY, String(v));
  } catch {
    /* storage unavailable */
  }
}

export function mountToken(root, mintStr) {
  let mint;
  try {
    mint = new PublicKey(mintStr);
  } catch {
    root.innerHTML = `<section class="center-msg"><h1 class="display-md">Not a valid mint address</h1><p><a href="#/">Back to all wells</a></p></section>`;
    return () => {};
  }
  const mintB58 = mint.toBase58();
  const well = wellPda(mint);

  root.innerHTML = `
  <section class="token-page">
    <div class="token-head">
      <img class="token-img" id="t-img" alt="" src="${placeholderArt(mintB58, "")}" />
      <div class="token-id">
        <h1 class="display-md"><span id="t-name">Loading…</span> <span class="sym" id="t-sym"></span></h1>
        <div class="token-meta">
          <button class="copy mono" id="t-mint" title="Copy mint address">${esc(short(mintB58, 6))} ⧉</button>
          <span class="muted">by <a id="t-creator" target="_blank" rel="noopener" href="#">-</a></span>
          <span class="links" id="t-links"></span>
        </div>
        <p class="token-desc" id="t-desc"></p>
      </div>
    </div>

    <div class="token-grid">
      <div class="col-stage">
        <div class="stage panel">
          <canvas class="well-canvas token-canvas" id="well-canvas" role="img" aria-label="This token's well. The water level shows the SOL in the well; every buy drops water in."></canvas>
          <div class="stage-overlay">
            <div class="gauge-label">Well level</div>
            <div class="gauge-value"><span id="w-level">-</span> <small>SOL</small></div>
            <div class="gauge-sub"><span id="w-drops">-</span> drops · <span id="w-total">-</span> SOL poured</div>
          </div>
          <div class="stage-hint muted small">Tap the water</div>
        </div>
        <div class="fee-meter panel" id="fee-meter">
          <div class="fee-top">
            <span class="fee-title" id="fee-title">Current fee</span>
            <span class="fee-now mono" id="fee-now">-</span>
          </div>
          <div class="fee-bar"><div class="fee-fill" id="fee-fill"></div></div>
          <div class="fee-sub muted small" id="fee-sub"></div>
        </div>
        <div class="stats panel">
          <div><div class="k">Price</div><div class="v mono" id="s-price">-</div><div class="muted small">SOL per token</div></div>
          <div><div class="k">Market cap</div><div class="v mono" id="s-mcap">-</div><div class="muted small">SOL</div></div>
          <div><div class="k">Volume</div><div class="v mono" id="s-vol">-</div><div class="muted small">SOL</div></div>
          <div class="span2">
            <div class="k">Curve progress <span class="muted" id="s-prog-t"></span></div>
            <div class="progress big"><div class="progress-bar" id="s-prog" style="width:0"></div></div>
            <div class="muted small" id="s-prog-sub"></div>
          </div>
        </div>
      </div>

      <aside class="col-side">
        <div class="panel trade" id="trade">
          <div class="seg" role="tablist" aria-label="Trade side">
            <button class="seg-btn active buy" data-side="buy" role="tab" aria-selected="true">Buy</button>
            <button class="seg-btn sell" data-side="sell" role="tab" aria-selected="false">Sell</button>
            <button class="icon-btn" id="slip-btn" aria-label="Slippage settings" title="Slippage">⚙ <span id="slip-val"></span></button>
          </div>
          <div class="slip-pop" id="slip-pop" hidden>
            <div class="k">Max slippage</div>
            <div class="chips">
              <button class="chip" data-slip="50">0.5%</button>
              <button class="chip" data-slip="100">1%</button>
              <button class="chip" data-slip="300">3%</button>
              <button class="chip" data-slip="500">5%</button>
              <button class="chip" data-slip="1000">10%</button>
            </div>
            <label class="input-unit small"><input id="slip-custom" inputmode="decimal" placeholder="Custom" /><b>%</b></label>
          </div>
          <label class="field">
            <span id="amt-label">You pay</span>
            <div class="input-unit big"><input id="amt" inputmode="decimal" placeholder="0.0" autocomplete="off" aria-label="Amount" /><b id="amt-unit">SOL</b></div>
          </label>
          <div class="chips" id="quick"></div>
          <div class="quote" id="t-quote" aria-live="polite"></div>
          <button class="btn btn-primary btn-block" id="trade-btn">Buy</button>
          <div class="muted small" id="bal-line"></div>
        </div>

        <div class="panel wellbox">
          <div class="wellbox-head">
            <h3>Holders' well</h3>
            <span class="muted small">Stake tokens, drink the water</span>
          </div>
          <div class="wb-stats">
            <div><div class="k">Your stake</div><div class="v mono" id="b-staked">-</div></div>
            <div><div class="k">Your share</div><div class="v mono" id="b-share">-</div></div>
            <div class="wide"><div class="k">Pending water</div><div class="v mono cyan big" id="b-pending">-</div></div>
            <div><div class="k">Total staked</div><div class="v mono" id="b-total">-</div></div>
            <div><div class="k">You claimed</div><div class="v mono" id="b-claimed">-</div></div>
            <div><div class="k">In reservoir</div><div class="v mono" id="b-res">-</div></div>
            <div><div class="k">Flowed to holders</div><div class="v mono cyan" id="b-flowed">-</div></div>
          </div>
          <div class="muted small" id="b-idle"></div>
          <label class="field">
            <span>Amount <em id="b-avail"></em></span>
            <div class="input-unit"><input id="b-amt" inputmode="decimal" placeholder="0" autocomplete="off" aria-label="Token amount to stake or unstake" /><button class="max" id="b-max" type="button">MAX</button></div>
          </label>
          <div class="btn-row">
            <button class="btn btn-secondary" id="b-stake">Stake</button>
            <button class="btn btn-ghost" id="b-unstake">Unstake</button>
          </div>
          <button class="btn btn-primary btn-block" id="b-claim">Claim water</button>
          <p class="muted small">Minimum stake is 1 whole token. Staked tokens can be withdrawn at any time; pending water is kept when you unstake.</p>
        </div>
      </aside>
    </div>

    <div class="panel feed">
      <div class="feed-head"><h3>Recent trades</h3><span class="live-dot" id="live-dot" title="Live">LIVE</span></div>
      <div class="feed-table" role="table" aria-label="Recent trades">
        <div class="feed-row feed-th" role="row"><span>Side</span><span>Trader</span><span>SOL</span><span>Tokens</span><span>Fee</span><span>To well</span><span>Age</span></div>
        <div id="feed-body"><div class="feed-empty muted">Listening to the well…</div></div>
      </div>
    </div>
  </section>`;

  const scene = new WellScene($("#well-canvas", root), { hero: false, initialLevel: 0.15, seed: mintB58.charCodeAt(0) * 97 + mintB58.charCodeAt(5) });
  let S = null; // latest on-chain state
  let side = "buy";
  let slippage = loadSlippage();
  let stopped = false;
  let dropsInFlight = 0;
  let newestSig = null;
  const seen = new Set();
  let trades = [];
  let timers = [];
  let subId = null;

  // ------------------------------------------------------------- header / metadata
  $("#t-mint", root).addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(mintB58);
      toast({ kind: "success", title: "Mint address copied", timeout: 2500 });
    } catch {
      toast({ kind: "info", title: mintB58, timeout: 6000 });
    }
  });

  async function renderMeta() {
    if (!S?.meta) return;
    $("#t-name", root).textContent = S.meta.name || "Unnamed";
    $("#t-sym", root).textContent = `$${S.meta.symbol || ""}`;
    document.title = `${S.meta.name} ($${S.meta.symbol}) - THE WATER WELL`;
    $("#amt-unit", root).textContent = side === "buy" ? "SOL" : S.meta.symbol || "TOKEN";
    const j = await fetchJsonMeta(S.meta.uri);
    if (!j || stopped) return;
    const img = safeUrl(j.image);
    if (img) $("#t-img", root).src = img;
    $("#t-desc", root).textContent = typeof j.description === "string" ? j.description : "";
    const ex = j.extensions || {};
    const links = [
      ["X", safeUrl(ex.twitter || j.twitter)],
      ["Telegram", safeUrl(ex.telegram || j.telegram)],
      ["Website", safeUrl(ex.website || j.website || j.external_url)],
    ].filter(([, u]) => u);
    $("#t-links", root).innerHTML = links.map(([n, u]) => `<a class="link-pill" href="${esc(u)}" target="_blank" rel="noopener nofollow">${n} ↗</a>`).join("");
  }

  // ------------------------------------------------------------- state refresh
  let loadFailures = 0;
  async function refresh() {
    try {
      const next = await fetchTokenState(mint, walletKey());
      if (stopped) return;
      if (!next.launch || !next.curve || !next.well) {
        if (!S) {
          root.innerHTML = next.legacy
            ? `<section class="center-msg"><h1 class="display-md">This well is from an older version</h1>
          <p class="muted">It was created before the latest program upgrade and can no longer be shown here.</p>
          <p><a class="btn btn-ghost" href="#/">Back to all wells</a></p></section>`
            : `<section class="center-msg"><h1 class="display-md">No well found for this mint</h1>
          <p class="muted">It may not be a THE WATER WELL token, or it was just created and the RPC has not caught up yet.</p>
          <p><a class="btn btn-ghost" href="#/">Back to all wells</a></p></section>`;
          stopped = true;
        }
        return;
      }
      const first = !S;
      S = next;
      if (first) {
        renderMeta();
        $("#t-creator", root).textContent = short(S.launch.creator);
        $("#t-creator", root).href = explorerAddr(S.launch.creator.toBase58());
        renderQuick();
      }
      renderStats();
      renderWellBox();
      renderQuote();
      if (dropsInFlight === 0) setWater(S.well.level, first);
    } catch (e) {
      console.warn("refresh failed", e);
      if (!S && !stopped) {
        loadFailures++;
        if (loadFailures >= 2) {
          $("#t-name", root).textContent = "Could not load this well";
          $("#t-desc", root).textContent = `${describeError(e)} Retrying…`;
        }
      }
    }
  }

  function setWater(levelLamports, instant = false) {
    const sol = Number(levelLamports) / 1e9;
    scene.setLevel(levelToFraction(sol, 25), { instant });
    $("#w-level", root).textContent = fmtSol(levelLamports, 4);
  }

  function renderStats() {
    const c = S.curve;
    $("#s-price", root).textContent = fmtPrice(priceSol(c));
    $("#s-mcap", root).textContent = fmtNum(marketCapSol(c), 2);
    $("#s-vol", root).textContent = fmtNum(Number(c.volume) / 1e9, 3);
    const prog = curveProgress(c);
    $("#s-prog", root).style.width = `${Math.max(0.4, prog * 100).toFixed(3)}%`;
    $("#s-prog-t", root).textContent = fmtPct(prog, 2);
    $("#s-prog-sub", root).textContent = `${fmtTokens(c.totalSupply - c.realTokens, 2)} of ${fmtTokens(c.totalSupply, 0)} tokens sold · ${fmtSol(c.realSol, 3)} SOL in the curve`;
    $("#w-drops", root).textContent = String(S.well.drops);
    $("#w-total", root).textContent = fmtSol(S.well.waterTotal, 4);
    if (dropsInFlight === 0) $("#w-level", root).textContent = fmtSol(S.well.level, 4);
    $("#bal-line", root).textContent = walletKey()
      ? `Wallet: ${fmtSol(S.solBalance, 4)} SOL · ${fmtTokens(S.tokenBalance, 2)} ${S.meta?.symbol || "tokens"}`
      : "";
  }

  // ------------------------------------------------------------- fee meter
  function tickFee() {
    if (!S) return;
    const L = S.launch;
    const now = chainNow();
    const fee = currentFeeBps(L, now);
    const elapsed = now - L.launchTimestamp;
    const open = elapsed < L.decaySeconds && L.startFeeBps > L.baseFeeBps;
    const frac = open ? Math.max(0, 1 - elapsed / L.decaySeconds) : 0;
    $("#fee-now", root).textContent = fmtBps(fee);
    $("#fee-fill", root).style.width = `${(frac * 100).toFixed(2)}%`;
    const meter = $("#fee-meter", root);
    meter.classList.toggle("hot", open);
    if (open) {
      $("#fee-title", root).textContent = "Anti-sniper fee";
      $("#fee-sub", root).textContent = `Falls from ${fmtBps(L.startFeeBps)} to ${fmtBps(L.baseFeeBps)} in ${Math.ceil(L.decaySeconds - elapsed)}s. Everything above ${fmtBps(L.platformFeeBps)} goes into the well.`;
    } else {
      $("#fee-title", root).textContent = "Current fee";
      $("#fee-sub", root).textContent = `${fmtBps(L.platformFeeBps)} to the platform, ${fmtBps(L.baseFeeBps - L.platformFeeBps)} into the well. The anti-sniper window has closed.`;
    }
  }

  // ------------------------------------------------------------- trade panel
  function setSide(s) {
    side = s;
    root.querySelectorAll(".seg-btn").forEach((b) => {
      b.classList.toggle("active", b.dataset.side === s);
      b.setAttribute("aria-selected", b.dataset.side === s ? "true" : "false");
    });
    $("#amt-label", root).textContent = s === "buy" ? "You pay" : "You sell";
    $("#amt-unit", root).textContent = s === "buy" ? "SOL" : S?.meta?.symbol || "TOKEN";
    const btn = $("#trade-btn", root);
    btn.textContent = s === "buy" ? "Buy" : "Sell";
    btn.classList.toggle("sell", s === "sell");
    $("#amt", root).value = "";
    renderQuick();
    renderQuote();
  }
  root.querySelectorAll(".seg-btn").forEach((b) => b.addEventListener("click", () => setSide(b.dataset.side)));

  function renderQuick() {
    const q = $("#quick", root);
    if (side === "buy") {
      q.innerHTML = ["0.05", "0.1", "0.5", "1"].map((v) => `<button class="chip" data-v="${v}">${v} SOL</button>`).join("");
    } else {
      q.innerHTML = [25, 50, 75, 100].map((p) => `<button class="chip" data-p="${p}">${p === 100 ? "Max" : p + "%"}</button>`).join("");
    }
    q.querySelectorAll(".chip").forEach((c) =>
      c.addEventListener("click", () => {
        if (c.dataset.v) $("#amt", root).value = c.dataset.v;
        else if (S) {
          const amt = (S.tokenBalance * BigInt(c.dataset.p)) / 100n;
          $("#amt", root).value = formatUnits(amt, 6);
        }
        renderQuote();
      })
    );
  }

  function renderSlip() {
    $("#slip-val", root).textContent = `${(slippage / 100).toFixed(slippage % 100 ? 1 : 0)}%`;
    root.querySelectorAll("[data-slip]").forEach((c) => c.classList.toggle("active", Number(c.dataset.slip) === slippage));
  }
  $("#slip-btn", root).addEventListener("click", () => {
    const pop = $("#slip-pop", root);
    pop.hidden = !pop.hidden;
  });
  root.querySelectorAll("[data-slip]").forEach((c) =>
    c.addEventListener("click", () => {
      slippage = Number(c.dataset.slip);
      saveSlippage(slippage);
      $("#slip-custom", root).value = "";
      renderSlip();
      renderQuote();
    })
  );
  $("#slip-custom", root).addEventListener("input", (e) => {
    const v = parseFloat(e.target.value);
    if (v > 0 && v <= 50) {
      slippage = Math.round(v * 100);
      saveSlippage(slippage);
      renderSlip();
      renderQuote();
    }
  });
  renderSlip();
  $("#amt", root).addEventListener("input", renderQuote);

  function currentQuote() {
    if (!S) return null;
    const raw = $("#amt", root).value;
    if (!raw.trim()) return null;
    const fee = currentFeeBps(S.launch, chainNow());
    if (side === "buy") {
      const lam = parseUnits(raw, 9);
      if (lam === null) return { error: "Enter a valid amount." };
      if (lam === 0n) return null;
      const q = quoteBuy(S.curve, lam, fee, S.launch.platformFeeBps);
      const minOut = (q.tokens * BigInt(10_000 - slippage)) / 10_000n;
      return { ...q, feeBps: fee, minOut, amount: lam };
    }
    const t = parseUnits(raw, 6);
    if (t === null) return { error: "Enter a valid amount." };
    if (t === 0n) return null;
    const q = quoteSell(S.curve, t, fee, S.launch.platformFeeBps);
    const minOut = (q.net * BigInt(10_000 - slippage)) / 10_000n;
    return { ...q, feeBps: fee, minOut, amount: t };
  }

  function renderQuote() {
    const el = $("#t-quote", root);
    const q = currentQuote();
    if (!q) {
      el.innerHTML = "";
      return;
    }
    if (q.error) {
      el.innerHTML = `<div class="quote-row err">${esc(q.error)}</div>`;
      return;
    }
    const sym = S.meta?.symbol || "tokens";
    const rows = [];
    const spot = Number(S.curve.virtualSol) / Number(S.curve.virtualTokens);
    if (side === "buy") {
      const exec = Number(q.net) / Math.max(1, Number(q.tokens));
      const impact = exec / spot - 1;
      rows.push(`<div class="quote-big"><span class="mono">${fmtTokens(q.tokens, 2)}</span> ${esc(sym)}</div>`);
      rows.push(`<div class="quote-row"><span>Fee ${fmtBps(q.feeBps)}</span><span class="mono">${fmtSol(q.fee.total, 6)} SOL</span></div>`);
      rows.push(`<div class="quote-row sub"><span>↳ drops into the well</span><span class="mono cyan">${fmtSol(q.fee.water, 6)} SOL</span></div>`);
      rows.push(`<div class="quote-row"><span>Price impact</span><span class="mono ${impact > 0.05 ? "warn" : ""}">${fmtPct(impact, 2)}</span></div>`);
      rows.push(`<div class="quote-row"><span>Minimum received</span><span class="mono">${fmtTokens(q.minOut, 2)}</span></div>`);
      if (q.exceeds) rows.push(`<div class="quote-row err">Not enough tokens left on the curve. Lower the amount.</div>`);
      else if (q.zero) rows.push(`<div class="quote-row err">Too small to buy anything.</div>`);
      if (walletKey() && S.solBalance < q.amount + (S.ataExists ? 0n : ATA_RENT) + TX_FEE_BUFFER)
        rows.push(`<div class="quote-row err">Not enough SOL in your wallet.</div>`);
    } else {
      const exec = Number(q.gross) / Number(q.tokensIn);
      const impact = 1 - exec / spot;
      rows.push(`<div class="quote-big"><span class="mono">${fmtSol(q.net, 6)}</span> SOL</div>`);
      rows.push(`<div class="quote-row"><span>Fee ${fmtBps(q.feeBps)}</span><span class="mono">${fmtSol(q.fee.total, 6)} SOL</span></div>`);
      rows.push(`<div class="quote-row sub"><span>↳ drops into the well</span><span class="mono cyan">${fmtSol(q.fee.water, 6)} SOL</span></div>`);
      rows.push(`<div class="quote-row"><span>Price impact</span><span class="mono ${impact > 0.05 ? "warn" : ""}">${fmtPct(impact, 2)}</span></div>`);
      rows.push(`<div class="quote-row"><span>Minimum received</span><span class="mono">${fmtSol(q.minOut, 6)} SOL</span></div>`);
      if (q.exceeds) rows.push(`<div class="quote-row err">The curve does not hold that much SOL.</div>`);
      else if (q.zero) rows.push(`<div class="quote-row err">Too small to sell.</div>`);
      if (walletKey() && q.amount > S.tokenBalance) rows.push(`<div class="quote-row err">You only have ${fmtTokens(S.tokenBalance, 4)} ${esc(sym)}.</div>`);
    }
    if (q.feeBps > S.launch.baseFeeBps) rows.push(`<div class="quote-row warn small">Anti-sniper fee is active. Waiting a few seconds lowers it.</div>`);
    el.innerHTML = rows.join("");
  }

  $("#trade-btn", root).addEventListener("click", async (e) => {
    if (!walletKey()) {
      try {
        await connectWallet();
        await refresh();
      } catch (err) {
        toast({ kind: "error", title: "Wallet", body: describeError(err) });
      }
      return;
    }
    const q = currentQuote();
    if (!q) return toast({ kind: "error", title: "Enter an amount" });
    if (q.error) return toast({ kind: "error", title: q.error });
    if (q.exceeds || q.zero) return toast({ kind: "error", title: "Adjust the amount", body: "This trade cannot be filled by the curve." });
    if (side === "sell" && q.amount > S.tokenBalance) return toast({ kind: "error", title: "Not enough tokens" });
    if (side === "buy" && S.solBalance < q.amount + (S.ataExists ? 0n : ATA_RENT) + TX_FEE_BUFFER)
      return toast({ kind: "error", title: "Not enough SOL", body: "Leave a little SOL for network fees." });
    const res = await runTx(e.currentTarget, side === "buy" ? "Buy" : "Sell", (onStage) =>
      swap({ mint, side, amount: q.amount, minOut: q.minOut, onStage })
    );
    if (res) {
      $("#amt", root).value = "";
      renderQuote();
      pollTrades();
      refresh();
    }
  });

  // ------------------------------------------------------------- holders' well
  function renderWellBox() {
    const w = S.well;
    const b = S.bucket;
    const sym = S.meta?.symbol || "tokens";
    $("#b-total", root).textContent = fmtTokens(w.totalStaked, 2);
    const connected = !!walletKey();
    const staked = b?.amount || 0n;
    const pending = pendingWater(b, w, chainNow());
    $("#b-staked", root).textContent = connected ? fmtTokens(staked, 2) : "-";
    $("#b-share", root).textContent = connected && w.totalStaked > 0n ? fmtPct(Number((staked * 1_000_000n) / w.totalStaked) / 1_000_000, 2) : connected ? "0%" : "-";
    $("#b-pending", root).textContent = connected ? `${fmtSol(pending, 6)} SOL` : "-";
    $("#b-claimed", root).textContent = connected ? `${fmtSol(b?.totalClaimed || 0n, 4)} SOL` : "-";
    $("#b-avail", root).textContent = connected ? `wallet ${fmtTokens(S.tokenBalance, 2)} · staked ${fmtTokens(staked, 2)} ${esc(sym)}` : "";
    $("#b-idle", root).textContent =
      w.totalStaked === 0n
        ? "Nobody is in the well, so the drip clock is paused. Stored water is not handed to the first staker; it starts draining to holders gradually (about an hour time constant) once someone is in."
        : "Water drains from the reservoir to holders gradually, roughly an hour time constant, and only while someone is in the well.";
    $("#b-claim", root).disabled = !connected || pending === 0n;
    $("#b-unstake", root).disabled = connected && staked === 0n;
    tickWater();
  }

  // live drip: re-simulate drip(now) on top of the on-chain state and ease the counters toward it
  let shownPending = 0;
  let shownRes = 0;
  function tickWater() {
    if (!S) return;
    const w = S.well;
    const sim = previewDrip(w, chainNow());
    const connected = !!walletKey();
    const pending = Number(pendingWater(S.bucket, w, chainNow())) / 1e9;
    const res = Number(sim.reservoir) / 1e9;
    const flowed = Number(w.waterTotal - sim.reservoir) / 1e9;
    const ease = (from, to) => (Math.abs(to - from) < 1e-10 || prefersReducedMotion() ? to : from + (to - from) * 0.35);
    shownPending = ease(shownPending, pending);
    shownRes = ease(shownRes, res);
    $("#b-pending", root).textContent = connected ? `${shownPending.toFixed(9)} SOL` : "-";
    $("#b-res", root).textContent = `${fmtNum(shownRes, 6)} SOL`;
    $("#b-flowed", root).textContent = `${fmtNum(Math.max(0, flowed), 6)} SOL`;
    $("#b-claim", root).disabled = !connected || pending === 0;
  }

  $("#b-max", root).addEventListener("click", () => {
    if (!S) return;
    // MAX fills what you can stake; if you have nothing in the wallet, fill what you can unstake.
    const v = S.tokenBalance > 0n ? S.tokenBalance : S.bucket?.amount || 0n;
    $("#b-amt", root).value = formatUnits(v, 6);
  });

  async function ensureWallet() {
    if (walletKey()) return true;
    try {
      await connectWallet();
      await refresh();
      return true;
    } catch (err) {
      toast({ kind: "error", title: "Wallet", body: describeError(err) });
      return false;
    }
  }

  $("#b-stake", root).addEventListener("click", async (e) => {
    if (!(await ensureWallet()) || !S) return;
    const amt = parseUnits($("#b-amt", root).value, 6);
    if (!amt) return toast({ kind: "error", title: "Enter how many tokens to stake" });
    if (amt > S.tokenBalance) return toast({ kind: "error", title: "Not enough tokens in your wallet" });
    if ((S.bucket?.amount || 0n) + amt < MIN_STAKE) return toast({ kind: "error", title: "Stake must be at least 1 whole token" });
    const btn = e.currentTarget;
    const res = await runTx(btn, "Stake", (onStage) => stake({ mint, amount: amt, onStage }));
    if (res) {
      $("#b-amt", root).value = "";
      scene.ripple(0, 0, 1.2);
      refresh();
    }
  });

  $("#b-unstake", root).addEventListener("click", async (e) => {
    if (!(await ensureWallet()) || !S) return;
    const staked = S.bucket?.amount || 0n;
    let amt = parseUnits($("#b-amt", root).value, 6);
    if (!amt) amt = staked;
    if (!amt) return toast({ kind: "error", title: "You have nothing staked" });
    if (amt > staked) return toast({ kind: "error", title: "You don't have that many tokens in the well" });
    const rest = staked - amt;
    if (rest > 0n && rest < MIN_STAKE)
      return toast({ kind: "error", title: "Leave at least 1 token or unstake everything", body: "What stays in the well must be zero or at least 1 whole token." });
    const res = await runTx(e.currentTarget, "Unstake", (onStage) => unstake({ mint, amount: amt, onStage }));
    if (res) {
      $("#b-amt", root).value = "";
      refresh();
    }
  });

  $("#b-claim", root).addEventListener("click", async (e) => {
    if (!(await ensureWallet()) || !S) return;
    const res = await runTx(e.currentTarget, "Claim", (onStage) => claim({ mint, onStage }));
    if (res) {
      scene.splashSmall();
      refresh();
    }
  });

  // ------------------------------------------------------------- live trades (signature polling + log events)
  function addTrade(t) {
    if (seen.has(t.key)) return false;
    seen.add(t.key);
    trades.push(t);
    trades.sort((a, b) => b.ts - a.ts || b.slot - a.slot);
    trades = trades.slice(0, 40);
    return true;
  }

  function animateTrade(t) {
    if (t.isBuy) {
      dropsInFlight++;
      const size = Math.min(2.2, 0.6 + Math.log10(1 + Number(t.sol) / 1e7) * 0.6);
      scene.drop({
        size,
        onImpact: () => {
          dropsInFlight = Math.max(0, dropsInFlight - 1);
          setWater(dropsInFlight === 0 && S ? S.well.level : t.wellLevel);
          pulseRow(t.key);
        },
      });
    } else {
      scene.splashSmall();
      setWater(t.wellLevel);
      pulseRow(t.key);
    }
  }

  function pulseRow(key) {
    const row = root.querySelector(`[data-key="${CSS.escape(key)}"]`);
    if (row) {
      row.classList.remove("flash");
      void row.offsetWidth;
      row.classList.add("flash");
    }
  }

  function renderFeed() {
    const body = $("#feed-body", root);
    if (!body) return;
    if (!trades.length) {
      body.innerHTML = `<div class="feed-empty muted">No trades yet. The first buy will drop the first water.</div>`;
      return;
    }
    const now = chainNow();
    body.innerHTML = trades
      .map(
        (t) => `
      <a class="feed-row ${t.isBuy ? "is-buy" : "is-sell"}" role="row" data-key="${esc(t.key)}" href="${esc(explorerTx(t.sig))}" target="_blank" rel="noopener">
        <span><b class="side-tag">${t.isBuy ? "BUY" : "SELL"}</b></span>
        <span class="mono">${esc(short(t.trader))}</span>
        <span class="mono">${fmtSol(t.sol, 4)}</span>
        <span class="mono">${fmtTokens(t.tokens, 1)}</span>
        <span class="mono ${t.feeBps > 400 ? "warn" : ""}">${fmtBps(t.feeBps)}</span>
        <span class="mono cyan">+${fmtSol(t.water, 5)}</span>
        <span class="mono muted age" data-ts="${t.ts}">${fmtAge(now - t.ts)}</span>
      </a>`
      )
      .join("");
  }

  async function fetchEventsFor(sigInfos) {
    // fetch transactions one by one (some RPCs reject JSON-RPC batches), oldest first
    const out = [];
    const list = sigInfos.filter((s) => !s.err).reverse();
    const results = await Promise.all(
      list.map((s) =>
        connection
          .getTransaction(s.signature, { commitment: "confirmed", maxSupportedTransactionVersion: 0 })
          .then((tx) => ({ s, tx }))
          .catch(() => ({ s, tx: null }))
      )
    );
    for (const { s, tx } of results) {
      const logs = tx?.meta?.logMessages;
      if (!logs) continue;
      for (const ev of parseEvents(logs)) out.push({ ev, sig: s.signature, slot: s.slot, blockTime: s.blockTime ?? tx.blockTime });
    }
    return out;
  }

  let polling = false;
  let failures = 0;
  let initDone = false;
  async function pollTrades() {
    if (polling || stopped) return;
    polling = true;
    const initial = !initDone; // the first successful poll only fills the feed, no animations
    try {
      const opts = { limit: initial ? 25 : 20 };
      if (newestSig && !initial) opts.until = newestSig;
      const sigs = await connection.getSignaturesForAddress(well, opts, "confirmed");
      failures = 0;
      $("#live-dot", root)?.classList.remove("off");
      if (!sigs.length) {
        initDone = true;
        if (initial) renderFeed();
        return;
      }
      newestSig = sigs[0].signature;
      const evs = await fetchEventsFor(sigs);
      let touched = false;
      const fresh = [];
      for (const { ev, sig, slot, blockTime } of evs) {
        const d = ev.data;
        if (d.mint && !d.mint.equals(mint)) continue;
        if (ev.name === "swapExecuted") {
          const t = {
              key: `${sig}:${d.isBuy ? "b" : "s"}:${d.tokenAmount.toString()}`,
              sig,
              slot,
              trader: d.trader.toBase58(),
              isBuy: d.isBuy,
              sol: BigInt(d.solAmount.toString()),
              tokens: BigInt(d.tokenAmount.toString()),
              feeBps: d.feeBps,
              water: BigInt(d.waterLamports.toString()),
              wellLevel: BigInt(d.wellLevel.toString()),
              ts: Number(d.timestamp.toString()) || blockTime || chainNow(),
          };
          if (addTrade(t) && !initial) fresh.push(t);
          touched = true;
        } else if (ev.name === "waterClaimed" || ev.name === "bucketChanged") {
          touched = true;
          if (!initial && ev.name === "waterClaimed") scene.ripple(0, -0.2, 0.6);
        }
      }
      initDone = true;
      renderFeed();
      // stagger the droplets so a burst of buys rains into the well one after another
      fresh.forEach((t, i) => setTimeout(() => !stopped && animateTrade(t), i * 280));
      if (touched && !initial) refresh();
    } catch (e) {
      failures++;
      if (failures > 2) $("#live-dot", root)?.classList.add("off");
      console.warn("trade poll failed", e);
    } finally {
      polling = false;
    }
  }

  // websocket fast path (optional): wake the poller as soon as the well account changes.
  // Polling above stays on regardless; the subscription is only used if the RPC supports it.
  wsAccountSubscribeSupported().then((ok) => {
    if (!ok || stopped) return;
    try {
      subId = connection.onAccountChange(
        well,
        (info) => {
          const w = parseWell(info);
          if (w && S && w.waterTotal !== S.well.waterTotal) pollTrades();
        },
        { commitment: "confirmed" }
      );
    } catch {
      subId = null;
    }
  });
  // ------------------------------------------------------------- lifecycle
  const offWallet = onWalletChange(() => refresh());
  (async () => {
    await syncClock();
    await refresh();
    if (stopped) return;
    await pollTrades();
    tickFee();
  })();

  const every = (ms, fn) => {
    const t = setInterval(() => {
      if (!document.hidden && !stopped) fn();
    }, ms);
    timers.push(t);
  };
  every(2500, () => pollTrades());
  every(10_000, () => refresh());
  every(250, tickFee);
  every(200, tickWater);
  every(1000, () => {
    // keep ages and the fee-sensitive quote fresh
    const now = chainNow();
    root.querySelectorAll(".age[data-ts]").forEach((el) => (el.textContent = fmtAge(now - Number(el.dataset.ts))));
    if (S && currentFeeBps(S.launch, chainNow()) > S.launch.baseFeeBps) renderQuote();
  });

  return () => {
    stopped = true;
    timers.forEach(clearInterval);
    offWallet();
    if (subId !== null) connection.removeAccountChangeListener(subId).catch(() => {});
    scene.destroy();
  };
}
